import {
  ActionIcon,
  Flex,
  Loader,
  MantineTheme,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { BiEdit } from "react-icons/bi";
import { Fragment, useContext, useEffect } from "react";
import { AppContext } from "src/api/AppContext";
import { CLIENT_TAB_LIST, ClientCardBg } from "src/utils/constants";
import {
  BankAccountType,
  PaymentMethodType,
  PaymentType,
  useGetClientPaymentMethodsManyQuery,
} from "src/generated/graphql";
import { IconPencil, IconWifi } from "@tabler/icons";
import useAppMain from "src/hooks/useAppMain";
import { useHeaderContext } from "src/api/context/HeaderContext";

export type IClientPaymentMethod = {
  id: string;
  isPrimary: boolean;
  isComplete: boolean;
  name?: string | null;
  mask?: string | null;
  vendor: PaymentType;
  expiresAt?: Date;
  merchant?: string | null;
  type: PaymentMethodType;
  accountType?: BankAccountType;
  routingNumber?: string;
};

const handleEditCard = (id: string, isPrimary: boolean) => {};

const Card = (item: IClientPaymentMethod, theme: MantineTheme) => {
  const expiryDate = item.expiresAt && new Date(item.expiresAt);
  return (
    <Flex
      key={item?.id}
      style={{
        width: "350px",
        height: "190px",
        backgroundColor: theme.colors.primary[0],
        color: theme.colors.primaryTextColor[6],
        borderRadius: "15px",
        padding: "10px 20px",
      }}
      direction={"column"}
      justify={"space-between"}
    >
      <Flex justify={"space-between"}>
        <Text size={18} fw={500}>
          {item.type === PaymentMethodType.Card
            ? item?.merchant || ""
            : `ACH - ${item.routingNumber || ""}`}
        </Text>
        <ActionIcon
          size="md"
          variant="transparent"
          onClick={() => handleEditCard(item.id, item.isPrimary)}
          sx={{
            "&:hover": {
              backgroundColor: theme.colors.primary[0],
            },
          }}
        >
          <IconPencil color={theme.colors.primaryTextColor[6]} />
        </ActionIcon>
      </Flex>

      <Flex direction={"column"}>
        <Flex pb={20} justify={"space-between"}>
          <Text size={18} fw={500}>
            {`${
              item.type === PaymentMethodType.Card
                ? "**** **** ****"
                : `Ending in`
            } ${item.mask?.substring(item.mask?.length - 4)}`}
          </Text>
          {item.type === PaymentMethodType.Card && (
            <IconWifi style={{ transform: "rotate(90deg)" }} />
          )}
        </Flex>
        <Flex align="center" justify={"space-between"} pb={10}>
          <Text size={18} fw={500}>
            {item.name}
          </Text>
          {expiryDate && (
            <Text size={14}>
              Exp : {expiryDate.getUTCMonth()} / {expiryDate.getUTCFullYear()}
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

const PaymentMethods = () => {
  const theme = useMantineTheme();
  const { currentClient } = useContext(AppContext);
  const { updateHeaderContent } = useHeaderContext();

  const { data, loading, refetch } = useGetClientPaymentMethodsManyQuery({
    variables: {
      clientId: currentClient?.id || "",
    },
    skip: currentClient == undefined,
    fetchPolicy: "network-only",
  });

  useAppMain(CLIENT_TAB_LIST.PAYMENT_METHODS, true);
  useEffect(() => {
    updateHeaderContent(
      <BiEdit size={25} color={theme.colors.secondaryTextColor[6]} />
    );
  }, []);

  return (
    <Flex justify={"flex-start"} direction={"column"} w={"100%"}>
      <Text
        size={16}
        fw={600}
        mt={20}
        mb={10}
        px={10}
        style={{ borderRadius: "5px" }}
        bg={theme.colors.primary[6]}
        color={theme.colors.primaryTextColor[6]}
      >
        Primary Card
      </Text>

      <Flex style={{ paddingLeft: 10 }}>
        {loading ? (
          <Loader />
        ) : data?.clientPaymentMethodsMany ? (
          data.clientPaymentMethodsMany.map((paymentMethod) => {
            if (paymentMethod.isPrimary && paymentMethod.isComplete) {
              return Card(paymentMethod as IClientPaymentMethod, theme);
            }
          })
        ) : (
          "No Saved  Payment Methods"
        )}
      </Flex>

      <Text
        size={16}
        fw={600}
        mt={20}
        mb={10}
        px={10}
        style={{ borderRadius: "5px" }}
        bg={theme.colors.primary[6]}
        color={theme.colors.primaryTextColor[6]}
      >
        Other Cards
      </Text>

      <Flex style={{ paddingLeft: 10 }}>
        {loading ? (
          <Loader />
        ) : data?.clientPaymentMethodsMany ? (
          data.clientPaymentMethodsMany.map((paymentMethod) => {
            <Fragment key={paymentMethod.id}>
              {!paymentMethod.isPrimary && paymentMethod.isComplete && (
                <>{Card(paymentMethod as IClientPaymentMethod, theme)}</>
              )}
            </Fragment>;
          })
        ) : (
          "No Saved  Payment Methods"
        )}
      </Flex>
    </Flex>
  );
};

export default PaymentMethods;
